/**
 * Sysadmin Models → "Model Changes" (#1506).
 *
 * A chronological feed of every mutation to a model brand or engine.
 * Each row is an `argus_model.*` audit event emitted by the M6c admin
 * endpoints (create / update / engine load + probe / engine unload),
 * read back via GET /admin/platform/argus-models/history.
 *
 * Why this exists: the Models rail previously had a dead "Model Changes"
 * nav link with no page. Operators need an at-a-glance answer to "who
 * changed which model, when, and did the engine load succeed?" without
 * digging through the global Sysadmin Audit firehose.
 *
 * File-route note: this is a flat `models.history.tsx` (static segment)
 * so /sysadmin/models/history dispatches ahead of the parametric
 * models/$id route — same convention as models.assignments.tsx.
 */

import { Fragment, useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  History, RefreshCw, AlertTriangle, Clock, ChevronLeft,
  ChevronDown, ChevronRight, PlusCircle, Pencil, Power, PowerOff,
  CheckCircle2, XCircle, Boxes,
} from 'lucide-react';
import {
  Card, CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  useArgusModelHistory,
  type ModelChangeRow,
} from '@/lib/use-argus-models';

export const Route = createFileRoute('/sysadmin/models/history')({
  component: ModelChangesPage,
});

// ─── Action → human label + icon ──────────────────────────────────────────────
//
// The raw verbs (argus_model.engine_load_started, …) are precise but
// unfriendly. Map each to a plain-language label + an icon so the feed
// reads like a changelog. Unknown verbs fall back to a de-prefixed,
// de-underscored rendering so a new action type still shows something
// sensible before this map is updated.
const ACTION_META: Record<string, { label: string; icon: typeof PlusCircle }> = {
  'argus_model.created': { label: 'Model created', icon: PlusCircle },
  'argus_model.updated': { label: 'Model updated', icon: Pencil },
  'argus_model.engine_load_started': { label: 'Engine load started', icon: Power },
  'argus_model.engine_load_probe_succeeded': { label: 'Engine load succeeded', icon: CheckCircle2 },
  'argus_model.engine_load_probe_failed': { label: 'Engine load failed', icon: XCircle },
  'argus_model.engine_unloaded': { label: 'Engine unloaded', icon: PowerOff },
};

function actionMeta(action: string) {
  const known = ACTION_META[action];
  if (known) return known;
  const pretty = action
    .replace(/^argus_model\./, '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
  return { label: pretty, icon: Boxes };
}

// Pull the friendliest model identifier out of the redacted summary blob.
function modelLabel(r: ModelChangeRow): string | null {
  const s = r.summary;
  if (s && typeof s === 'object') {
    const displayName = s['displayName'];
    if (typeof displayName === 'string' && displayName.length > 0) return displayName;
    const brandId = s['brandId'];
    if (typeof brandId === 'string' && brandId.length > 0) return brandId;
  }
  return null;
}

function ModelChangesPage() {
  // Keyset pagination: `cursor` drives the active request; `accumulated`
  // stitches successive pages together so "Load more" appends rather
  // than replaces. Matches the audit.sysadmin.tsx pattern.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [accumulated, setAccumulated] = useState<ModelChangeRow[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading, error, refetch, isFetching } = useArgusModelHistory(cursor);

  useEffect(() => {
    if (!data) return;
    setAccumulated((prev) => {
      if (!cursor) return data.items; // fresh load / refresh
      const seen = new Set(prev.map((r) => r.id));
      const out = [...prev];
      for (const r of data.items) if (!seen.has(r.id)) out.push(r);
      return out;
    });
  }, [data, cursor]);

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const refresh = () => {
    setCursor(undefined);
    refetch();
  };
  const loadMore = () => data?.nextCursor && setCursor(data.nextCursor);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <History className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Model Changes</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Every create, update, and engine load / unload across the model catalog.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/sysadmin/models">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" />
              Models
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'h-3.5 w-3.5 mr-1.5 animate-spin' : 'h-3.5 w-3.5 mr-1.5'} />
            Refresh
          </Button>
        </div>
      </div>

      {isLoading && accumulated.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading model changes…
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load model changes: {error.message}</div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border">
          {/* SYSADMIN-PLATFORM-SPEC §11.5: sysadmin tables use a flat
              bordered container, never a card wrapper. */}
          <div className="px-4 py-3 border-b">
            <h2 className="text-base font-semibold">Change history</h2>
            <p className="text-sm text-muted-foreground">
              Showing {accumulated.length} change{accumulated.length === 1 ? '' : 's'}.
              {data?.nextCursor ? ' More available.' : ' End of history.'}
            </p>
          </div>
          <div>
            {accumulated.length === 0 ? (
              <div className="px-6 py-10 text-sm text-muted-foreground text-center">
                No model changes recorded yet. Creating a model or loading an
                engine from the Models rail will appear here.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Outcome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accumulated.map((r) => {
                    const meta = actionMeta(r.action);
                    const Icon = meta.icon;
                    const label = modelLabel(r);
                    const isOpen = expanded.has(r.id);
                    const hasDetail = !!r.summary && Object.keys(r.summary).length > 0;
                    const failed = r.outcome === 'failure' || r.action.endsWith('failed');
                    return (
                      <Fragment key={r.id}>
                        <TableRow>
                          <TableCell className="w-8 align-top">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => toggleExpanded(r.id)}
                              disabled={!hasDetail}
                              aria-label={isOpen ? 'Collapse details' : 'Expand details'}
                              aria-expanded={isOpen}
                              title={hasDetail ? 'Show change details' : 'No additional details'}
                            >
                              {isOpen ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                            <span
                              className="inline-flex items-center gap-1"
                              title={new Date(r.createdAt).toLocaleString()}
                            >
                              <Clock className="h-3 w-3" />
                              {new Date(r.createdAt).toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1.5">
                              <Icon
                                className={
                                  failed
                                    ? 'h-3.5 w-3.5 text-destructive'
                                    : 'h-3.5 w-3.5 text-muted-foreground'
                                }
                              />
                              <span className="text-sm">{meta.label}</span>
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">
                            {label ? (
                              <span className="font-medium truncate max-w-[200px] block" title={label}>
                                {label}
                              </span>
                            ) : (
                              <code className="text-[11px] text-muted-foreground">
                                {r.resourceType}
                                {r.resourceId ? ` · ${r.resourceId.slice(0, 8)}` : ''}
                              </code>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.actorEmail ? (
                              <span className="truncate max-w-[180px] block" title={r.actorEmail}>
                                {r.actorEmail}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">system</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {/* Outcome-driven only — action type is conveyed by
                                the icon + label in the Change column, so the
                                Outcome badge stays semantically clean (success
                                vs failure), matching audit.sysadmin.tsx. */}
                            <Badge variant={failed ? 'destructive' : 'default'}>
                              {r.outcome ?? 'success'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                        {isOpen && hasDetail && (
                          <TableRow className="bg-muted/30">
                            <TableCell />
                            <TableCell colSpan={5} className="text-xs py-3">
                              <div className="text-muted-foreground mb-1">Details</div>
                              <pre className="font-mono text-[11px] bg-background border rounded px-2 py-1 overflow-x-auto">
{JSON.stringify(r.summary, null, 2)}
                              </pre>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {data?.nextCursor && (
              <div className="flex justify-center py-4">
                <Button variant="outline" size="sm" onClick={loadMore} disabled={isFetching}>
                  {isFetching ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
